import { routes, type ErrorBody, type RouteBody, type RouteName, type RouteResponse } from '@chapsi/contracts';
import { APPEARANCE_CACHE_KEY } from '../lib/theme.js';

/**
 * One typed client for every route in packages/contracts. The web app never sees a token: the API
 * sets HttpOnly cookies, and every write carries the CSRF header the API requires on cookie requests.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorBody['error']['code'] | 'network',
    message: string,
    readonly issues: { path: string; message: string }[] = [],
  ) {
    super(message);
  }
}

type Params = Record<string, string>;
type Query = Record<string, string | number | boolean | undefined | null>;

interface CallOptions<N extends RouteName> {
  params?: Params;
  query?: Query;
  body?: RouteBody<N>;
  signal?: AbortSignal;
  /** Extra request headers (a Showcase public link's password, docs/adr/0068). */
  headers?: Record<string, string>;
}

let refreshing: Promise<boolean> | null = null;
let onSignedOut: () => void = () => undefined;
export function setSignedOutHandler(fn: () => void): void {
  onSignedOut = fn;
}

async function refreshOnce(): Promise<boolean> {
  refreshing ??= fetch('/v1/auth/refresh', { method: 'POST', credentials: 'include', headers: { 'x-chapsi-csrf': '1' } })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => setTimeout(() => (refreshing = null), 0));
  return refreshing;
}

export async function api<N extends RouteName>(name: N, opts: CallOptions<N> = {}): Promise<RouteResponse<N>> {
  const def = routes[name];
  // Each person's look is their own (docs/adr/0075): the browser forgets the last one when its person signs out.
  if (name === 'signOut') {
    try {
      localStorage.removeItem(APPEARANCE_CACHE_KEY);
    } catch {
      /* private mode */
    }
  }
  const path = def.path.replace(/:([A-Za-z]+)/g, (_, key: string) => {
    const v = opts.params?.[key];
    if (!v) throw new Error(`Missing path parameter ${key} for ${name}`);
    return encodeURIComponent(v);
  });
  const qs = opts.query
    ? new URLSearchParams(Object.entries(opts.query).filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => [k, String(v)])).toString()
    : '';
  const url = qs ? `${path}?${qs}` : path;
  const init: RequestInit = {
    method: def.method,
    credentials: 'include',
    headers: {
      ...(def.method !== 'GET' ? { 'x-chapsi-csrf': '1' } : {}),
      ...(opts.body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...opts.headers,
    },
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
    ...(opts.signal ? { signal: opts.signal } : {}),
  };

  // Public routes, and the client portal's own link sessions (docs/adr/0027), are not app sign-ins: a 401
  // there is answered by the screen itself (the portal shows its sign-in card), never by refreshing the
  // app session or sending the person to the app's sign-in page.
  const appSession = def.isolation.kind !== 'public' && def.isolation.kind !== 'portal';
  let res: Response;
  try {
    res = await fetch(url, init);
    // Access cookies last 15 minutes; refresh once and retry, except for the auth routes themselves.
    if (res.status === 401 && appSession && name !== 'refresh') {
      if (await refreshOnce()) res = await fetch(url, init);
    }
  } catch {
    throw new ApiError(0, 'network', 'You appear to be offline. We will retry when you are back.');
  }
  if (res.status === 401 && appSession) onSignedOut();
  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : undefined;
  if (!res.ok) {
    const err = (json as ErrorBody | undefined)?.error;
    throw new ApiError(res.status, err?.code ?? 'internal', err?.message ?? 'Something went wrong', err?.issues ?? []);
  }
  return json as RouteResponse<N>;
}
